import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Home, Mail, Plus, UserRound } from 'lucide-react';
import { Fragment, lazy, Suspense, useCallback, useEffect } from 'react';
import { OnboardingScreen } from '@/screens/OnboardingScreen';
import { SignInScreen } from '@/screens/SignInScreen';
import { PullIndicator } from '@/components/PullIndicator';
import { Failed, Loading } from '@/components/states';
import { getUnreadCount } from '@/lib/api';
import { FEED_MATCH_ID } from '@/lib/feed-route';
import { forgetInvite, rememberInvite } from '@/lib/invite';
import { usePullToRefresh } from '@/lib/pull-to-refresh';
import { keys } from '@/lib/query-client';
import { useSession } from '@/lib/session';
import { cn } from '@/lib/utils';
import { Pigeon } from './Pigeon';

/* Public-only copy and its substantial bespoke stylesheet stay off the member
   entry path. A signed-in cold load should not pay for a surface it cannot see. */
const LandingScreen = lazy(() =>
  import('@/screens/LandingScreen').then((module) => ({
    default: module.LandingScreen,
  })),
);

/**
 * Four tabs, as docs/1 § Surfaces lists them.
 *
 * There is deliberately NO surface for browsing people, no deck, no card
 * stack, no swiping, no grid of candidates, no "people near you" (docs/5 D260730-2028).
 * People are met in public: in the feed, or in the comments under a letter.
 *
 * NO SEARCH TAB, and there is now a search field to explain that about. The feed
 * carries one, over ROOMS (docs/5 § D260803-1933) — a fifth tab for it would spend permanent
 * furniture on a lookup, and there is nothing else for such a tab to search: no
 * people search exists at any depth, and the Loft's field searches the people you
 * already write to.
 *
 * **Dove is third rather than second**, which is the whole of its priority: it
 * sits after the two tabs that carry the product and before the one that is
 * settings. It also carries NO BADGE, ever, a dot on this tab would be the
 * companion asking to be attended to, which is precisely the mechanic docs/5 D260731-1551
 * refuses.
 *
 * TWO TABS BADGE — `Loft` and `You` (docs/5 § D260812-1637, § D260808-2012) — see
 * `useUnreadDot`. They report two different facts and each is cleared by the act
 * that answers it: the Loft's dot means A LETTER HAS LANDED AND IS NOT OPENED and
 * goes out when it is opened; the You tab's means something happened in the open
 * and goes out when the notifications page is looked at.
 *
 * WHAT THE LOFT'S DOT IS NOT, because § D260808-2012 refused one on exactly these
 * grounds and only one of its objections survived being built. It is not a
 * duplicate of the row — the row says *Unopened* to somebody already on that
 * screen, and the dot exists for everybody who is not. It is not a queue with a
 * backlog: a dot, never a number, like the other one. It cannot report a letter in
 * the air, a reply owed or a draft left open, so it is never a mark somebody
 * cannot clear by doing the thing it asks (`letterIsWaiting` is the enforcement).
 * And it cannot be reached by the romance track or by anybody's interest in you
 * (I3): the only thing that lights it is a letter, from somebody you already
 * write to, that has already arrived.
 *
 * THE OTHER TWO STILL NEVER BADGE. The Feed cannot: a feed is never behind, and a
 * dot over it would be unread writing, which is the count docs/5 § D260801-1119
 * refuses. Dove cannot, ever (docs/5 D260731-1551), and that refusal is the one
 * this file is least willing to reopen.
 *
 * Still four, and third still means third: the create button that splits this
 * row in half (see `CreateButton`) is an action rather than a destination, and
 * it is not in this list because nothing about it is a tab.
 */
const TABS = [
  { to: '/', label: 'Feed', icon: Home },
  { to: '/loft', label: 'Loft', icon: Mail },
  { to: '/dove', label: 'Dove', icon: DoveTabIcon },
  { to: '/me', label: 'You', icon: UserRound },
] as const;

/**
 * Which fact each badged tab wears, by route. See `useUnreadDot`.
 *
 * A MAP RATHER THAN TWO CONSTANTS, so that "which tabs badge" is one list that
 * can be read at a glance and cannot grow a third entry without somebody
 * noticing they are adding one.
 */
const BADGED_TABS = {
  /* NOT "unread letters", AND NOT A COUNT. The dot is binary and so is the
     sentence — the same discipline the You tab's wording has carried since it
     was written: it says some, it never says how many. "Waiting" rather than
     "unopened" because the label is spoken after the tab's own name, and *Loft,
     a letter waiting* is a sentence; the row on the screen inside says
     `Unopened`, which is the word for a thing you are looking at. */
  '/loft': { fact: 'letters', said: 'a letter waiting' },
  '/me': { fact: 'you', said: 'some unseen notifications' },
} as const;

/**
 * Whether each badged tab wears its dot (docs/5 § D260808-2012, § D260812-1637).
 *
 * A BOOLEAN OUT OF A NUMBER, which is the same narrowing `ProfileScreen`'s bell
 * does at its own call site: `GET /notifications/unread` answers with a count, and
 * nothing in this product renders one (docs/5 § D260801-1119). A badge reading "12" over a
 * tab is an engagement figure wearing a different hat, and it turns a list of
 * things that already happened into a backlog.
 *
 * IT IS THE SAME CACHE ENTRY THE BELL READS, so the two never disagree and the
 * screen that clears the dot clears both: `NotificationsScreen` invalidates
 * `keys.unread` on arrival, and this hook is holding the query, so the dot goes out
 * under the tab bar in the same frame as the page draws. Only the interval belongs
 * here — the bell asks once because a card is a visit, and the bar is mounted for
 * as long as the app is open.
 *
 * ASKED AGAIN ON A TIMER, and this is the number that had to be chosen rather than
 * inherited. Without it the dot would answer only what was true when the app
 * started: nothing else invalidates this key, and the two triggers that already
 * exist — a pull-to-refresh (`refetchQueries({ type: 'active' })`) and the window
 * regaining focus — both require the person to do something first, which makes a
 * dot that appears only once you have already gone looking. A minute is coarse on
 * purpose. It never runs while the app is in the background (react-query pauses an
 * interval on a hidden document unless asked not to), so nothing here wakes a phone
 * or spends a radio on a dot nobody is looking at.
 */
const UNREAD_POLL_MS = 60_000;

/**
 * ONE QUERY, TWO BOOLEANS, and the second one costs no extra traffic
 * (docs/5 § D260812-1637). The endpoint answers both counts because the shell
 * already polls it; a hook of its own for the Loft would have doubled the poll to
 * ask a question the same round trip was already returning.
 *
 * THE LOFT'S DOT IS CLEARED BY A DIFFERENT ACT FROM THE OTHER ONE, so the two
 * halves of this answer are invalidated from different places: the notifications
 * page invalidates on arrival, and `CorrespondenceScreen` invalidates when it
 * opens a letter. Both land on `keys.unread`, which is the single cache entry
 * this hook and the bell both read — so neither dot can be stale while the other
 * is fresh.
 */
function useUnreadDot(enabled: boolean): Record<'letters' | 'you', boolean> {
  const { data } = useQuery({
    queryKey: keys.unread,
    queryFn: getUnreadCount,
    enabled,
    refetchInterval: UNREAD_POLL_MS,
  });
  return {
    letters: (data?.letters ?? 0) > 0,
    you: (data?.unread ?? 0) > 0,
  };
}

/**
 * The create button goes after this tab, which puts it in the middle of the bar
 * — the one spot in reach of either thumb without moving the hand holding the
 * phone.
 */
const CREATE_AFTER = 1;

/**
 * Routes that own the whole screen and hide the tab bar.
 *
 * Keep this list in step with the routes in `router.tsx`: a full-screen route
 * missing from here renders a tab bar under its footer, and the bug looks like a
 * spacing problem rather than a routing one.
 *
 * Matched on the pathname only, so `/compose?kind=letter` is covered without this
 * list having to know about search parameters.
 */
/*
 * `/me/edit` is here for the same reason `/compose` is: it is a form with unsaved
 * words in it, and the tab bar is a row of four ways to discard them by accident.
 * Both screens carry their own way out — one arrow, top start — so nothing is
 * trapped; what is removed is the tap that leaves without asking. It also means the
 * save button is the only thing along the bottom edge rather than a second bar
 * stacked on the tabs.
 */
const FULL_SCREEN: string[] = ['/compose', '/me/edit'];

/**
 * Routes that size themselves to the viewport and KEEP the tab bar.
 *
 * These get no tail gap either, for the same reason a full-screen route does not
 * (see the `pb-10` note below): a screen whose root is `h-full` resolves that
 * height against the scroller's CONTENT box, so 40px of padding does not sit under
 * the screen — it makes the screen 40px shorter and leaves the page background
 * showing between it and the tab bar. On `/dove` that band was a strip of chin
 * under the sill, visible whenever the sheet was closed and hidden when it was
 * open, because the sheet is `bg-background` too and the two read as one surface.
 *
 * Same warning as `FULL_SCREEN`: keep it in step with `router.tsx`. A new
 * viewport-height screen missing from here gets the chin back.
 */
const FILLS_VIEWPORT: string[] = ['/dove'];

/**
 * The prefix of the one route that is not behind the login wall (docs/5 § D260805-0328).
 *
 * A PREFIX RATHER THAN A LIST ENTRY, because the path carries a code. It answers
 * two questions and they are separate: whether the gate lets a stranger through
 * (`Gate`, below) and whether the tab bar is drawn (`isFullScreen`). Both are yes
 * here — a person with no account has nothing to navigate between, and a member
 * who lands on it is redirected off it within a frame, so a tab bar would flash.
 */
const JOIN_PREFIX = '/join/';
const ROUTE_CANARY_PREFIX = '/_canary';

/**
 * The invitation a stranger arrived on, written down before the gate decides
 * anything (docs/5 § D260805-0328).
 *
 * IT IS HERE RATHER THAN IN `JoinScreen` BECAUSE THE SCREEN DOES NOT ALWAYS RENDER.
 * The gate answers `needsOnboarding` before it answers "signed out", which is
 * correct — an account half made is not a stranger — but it meant somebody who
 * signed up with Google, abandoned the handle form, and later tapped their friend's
 * link never mounted `JoinScreen` at all, so the code was never stored and nobody
 * was credited. That is not a rare path: the abandoned step asks for a handle and a
 * date of birth. Storing from the shell makes the invitation independent of which
 * branch the gate takes.
 *
 * AND IT IS CLEARED THE MOMENT THERE IS A FINISHED ACCOUNT, which is the other half.
 * `OnboardingScreen` clears it on a successful claim, but a code also gets written
 * by opening a dead link, or by a member whose session had expired — and nothing
 * cleared those, so the stored value outlived its purpose: it kept `SignInScreen`
 * opening in create-account mode forever, and on a shared device the next person to
 * finish onboarding would have spent somebody else's stale invitation. A viewer
 * existing is proof this device's user is not a newcomer.
 */
function useInviteHandoff(pathname: string): void {
  const { viewer } = useSession();
  useEffect(() => {
    if (viewer) {
      forgetInvite();
      return;
    }
    const code = pathname.startsWith(JOIN_PREFIX)
      ? pathname.slice(JOIN_PREFIX.length)
      : null;
    /* Stored even when the lookup will 404: a code that does not resolve today may
       resolve in a minute, it is ignored rather than refused at onboarding, and the
       alternative is deciding here that somebody's invitation was worthless. */
    if (code) rememberInvite(code);
  }, [pathname, viewer]);
}

function isFullScreen(pathname: string): boolean {
  return (
    FULL_SCREEN.includes(pathname) ||
    pathname.startsWith(JOIN_PREFIX) ||
    pathname.startsWith(ROUTE_CANARY_PREFIX)
  );
}

/**
 * The name the app's one scroller answers to (docs/5 § D260809-0359).
 *
 * EXPORTED BECAUSE THE ROUTER NEEDS THE SAME STRING: it restores this element's
 * position on a back navigation and scrolls it to the top on a forward one, and
 * both are configured there by selector. Two spellings of one id would leave a
 * scroll cache filed under a name nothing reads.
 */
export const SCROLLER_ID = 'app';

/** Whether the route sizes itself to the viewport rather than scrolling in it. */
function ownsHeight(pathname: string): boolean {
  return isFullScreen(pathname) || FILLS_VIEWPORT.includes(pathname);
}

/**
 * The tab keeps the same rendered sparrow avatar as the rest of the product
 * identity. `strokeWidth` is accepted so the tab list can treat it like a Lucide
 * icon even though its pixels come from the shared brand artwork.
 */
function DoveTabIcon({
  strokeWidth: _strokeWidth,
  className,
}: { className?: string; strokeWidth?: number }) {
  /* `Pigeon` rather than a second `<img>` at the same path: it is the component
     that owns the artwork, its intrinsic size and its hairline, and a copy here
     was the one that would keep pointing at a retired bird. The tab's `size-*`
     wins over the component's own width through tailwind-merge.

     THE ACTIVE CUE IS OPACITY, because this bird is a fixed-palette PNG. The
     other three tabs go from `muted-foreground` to `foreground`, and an `<img>`
     inherits neither; without this the Dove tab is the only one that does not
     dim when you are somewhere else. `aria-current` is already on the link, so
     the state is read from the DOM rather than threaded through a second prop. */
  return (
    <Pigeon
      className={cn(
        'opacity-55 transition-opacity group-aria-[current=page]:opacity-100',
        className,
      )}
    />
  );
}

/**
 * The create control's footprint: 56×40, a C6 envelope's landscape ratio at the
 * width the round button already occupied.
 *
 * A number rather than `w-14 h-10`, because the RATIO is the decision and two
 * loose Tailwind sizes are two numbers somebody will round one of.
 */
const ENVELOPE = { width: 56, height: 40 } as const;

/**
 * Writing, from anywhere in the app.
 *
 * IT OPENS BOTH KINDS, which is what the label stopped promising when this stopped
 * being a post button: `/compose` is an open letter and a direct letter on one
 * surface, and the first thing on it is which (docs/5 § D260803-1551, § D260803-1952). The button
 * says "Write" rather than naming a kind for the same reason the screen it opens
 * has no title — naming one of two tabs from outside is wrong half the time.
 *
 * IT IS AN ENVELOPE RATHER THAN A CIRCLE (docs/5 § D260803-1953). A round button with a
 * plus in it is the create control the whole category ships, and it said nothing
 * about what this one makes; everything written in this app is a letter now
 * (§ D260803-1952), so the control is landscape at an envelope's proportions — `ENVELOPE`
 * — at the radius the app's own cards use. The plus is all that is drawn inside
 * it: a flap and an address line at this size would be a picture of an envelope
 * on a button, and the silhouette is the whole of what is being said.
 *
 * IT IS NOT A TAB AND IT IS DRAWN SO YOU CANNOT READ IT AS ONE: no active state,
 * and solid where the four tabs are outlines. The tabs are places you go and this
 * is the only thing in the row that does something, so the one that behaves
 * differently looks different. That distinction used to be carried partly by the
 * tabs having words under them and this not; with the words gone (§ D260803-1953) it rests
 * on the fill and the shape, which is why the shape had to become something other
 * than a rounder version of the pill behind an active tab.
 *
 * It also is not a floating action button, which is the thing the composer
 * prompt row it replaces was written to avoid: it does not hover over the feed,
 * does not follow you down the page, and never covers a letter. It sits in a bar
 * that was already there, and it asks for less of the screen than the row did —
 * a row that only the feed had, and that said "Say something ordinary" to
 * someone who had opened the app to read.
 *
 * Monochrome, inverted rather than coloured: index.css spends colour on things
 * that carry meaning, and contrast alone is enough to make the one filled shape
 * in a row of glyphs the thing your eye lands on.
 */
function CreateButton() {
  return (
    <Link
      to="/compose"
      aria-label="Write"
      className="group flex flex-1 items-center justify-center py-2"
    >
      <span
        className="flex items-center justify-center rounded-xl bg-foreground text-background transition-colors group-hover:bg-foreground/85"
        style={ENVELOPE}
      >
        <Plus aria-hidden className="size-[1.35rem]" strokeWidth={2.2} />
      </span>
    </Link>
  );
}

/**
 * THE LOGIN WALL, AND IT IS HERE BECAUSE THIS IS THE ROOT ROUTE'S COMPONENT.
 *
 * Every member route in the product is behind it — user content is never public or
 * indexable (docs/2 § Indexability). The signed-out root is intercepted by
 * `AppShell` before this shell is drawn and contains product explanation only
 * (docs/5 § D260901-1452); it is not a hole a route can opt itself into. Gating the
 * rest in one place means a screen added later is walled by default, which is the
 * direction a mistake should fall in.
 *
 * THAT INTERCEPTION WAITS FOR THIS GATE'S FIRST TWO ANSWERS (docs/5 § D260923-2002).
 * A session that has not resolved is not a stranger, so `/` holds the waiting and
 * unreachable branches below rather than guessing — which is the same reason the
 * third answer exists, stated one screen earlier.
 *
 * FOUR ANSWERS, AND THE THIRD IS THE ONE PRODUCTS GET WRONG:
 *
 *   - **Waiting.** Blocks, not a spinner (`Loading`), and never a blank frame.
 *   - **Unreachable.** The API is down or the origin is wrong; one line and a retry.
 *     A silent fall through to the sign-in screen would be worse than the truth: it
 *     tells somebody who IS signed in that they are not.
 *   - **Signed in, unfinished.** A distinct answer from signed out, because `/me`
 *     reports it distinctly and a client that cannot tell them apart loops between
 *     the two screens.
 *   - **Ready.** The tab shell.
 *
 * The gate replaces the WHOLE shell, tab bar included: there is nothing to navigate
 * between until there is an account, and a tab bar under the sign-in form would be
 * four dead targets.
 *
 * THERE IS NOW EXACTLY ONE HOLE IN IT, `open` (docs/5 § D260805-0328), and it is here rather
 * than in the route because that is what keeps the rule intact: a route cannot opt
 * itself out, so a screen added later is walled whether or not anybody thought
 * about it, and the list of exceptions is one line in one file.
 *
 * WHAT THE HOLE LETS THROUGH IS NOT USER CONTENT. `/join/:code` renders one
 * member's name and their bird to somebody holding a secret that member sent them
 * (see `JoinScreen`, and `InviteWelcome` for the closed list). Note where it sits:
 * BELOW the onboarding branch, so somebody signed in but unfinished still lands on
 * onboarding rather than on a welcome screen — an account half made is not a
 * stranger, and finishing it is the only thing worth doing next. Their invitation
 * is not lost by that, but the reason is `useInviteHandoff` and NOT this order: the
 * code is written down by the shell before any branch here runs, precisely because
 * this branch means `JoinScreen` may never mount.
 */
function Gate({
  children,
  open,
}: {
  children: React.ReactNode;
  /** This route may render without an account. See `JOIN_PREFIX`. */
  open: boolean;
}) {
  const { viewer, needsOnboarding, loading, error, refresh } = useSession();

  /* Each branch is its own flex column filling the shell, so the screens below can
     size against a definite height — `h-full` on the sign-in form is what puts its
     button at the bottom of the phone rather than under the fold. */
  const full = (node: React.ReactNode) => (
    <div className="flex min-h-0 flex-1 flex-col">{node}</div>
  );

  if (loading) return full(<Loading count={2} />);

  if (error) {
    return full(
      <div className="flex h-full items-center justify-center">
        <Failed onRetry={() => void refresh()} message="Can’t reach Dove." />
      </div>,
    );
  }

  if (needsOnboarding) return full(<OnboardingScreen />);
  if (!viewer) return open ? <>{children}</> : full(<SignInScreen />);
  return <>{children}</>;
}

export function AppShell() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const routeIds = useRouterState({
    select: (s) => s.matches.map((match) => match.routeId),
  });
  const joining = pathname.startsWith(JOIN_PREFIX);
  const checkingRoute = pathname.startsWith(ROUTE_CANARY_PREFIX);

  /* Independent of every branch below, so an invitation survives whichever screen
     the gate decides to show. See `useInviteHandoff`. */
  useInviteHandoff(pathname);

  /*
   * Writing takes the whole screen. A tab bar under a half-written post is an
   * invitation to leave mid-sentence, and it makes the screen's own Cancel
   * ambiguous about what it is cancelling. The draft is saved either way, but
   * the screen should still read as one task.
   */
  const fullScreen = isFullScreen(pathname);

  /*
   * THE DOTS ON THE LOFT AND YOU TABS (docs/5 § D260808-2012, § D260812-1637).
   *
   * `viewer` GATES THE REQUEST RATHER THAN THE RENDER. The bar itself is drawn
   * inside `Gate`, so it only ever exists for somebody signed in — but this hook
   * runs in the shell's body, which is a frame earlier and also runs behind the
   * sign-in screen. Without the flag the app asks for a stranger's notifications
   * and takes a 401 for it on every cold start.
   */
  const { viewer, needsOnboarding, loading, error } = useSession();
  const unread = useUnreadDot(Boolean(viewer));

  /*
   * PULLING THE TOP OF THE SCROLLER DOWN REFETCHES WHAT IS ON IT (docs/5 § D260806-1356).
   *
   * `type: 'active'` IS THE WHOLE OF THE DEFINITION, and it is why this lives here
   * rather than on five screens: the queries that are mounted are, by construction,
   * the ones this screen is showing — so the gesture means "again" on every tab,
   * including tabs added later, without the shell knowing a single query key.
   *
   * ONLY WHERE THE ROUTE IS ACTUALLY A SCROLL — `ownsHeight`, the same test the tail
   * gap uses, and it excludes two different kinds of screen for two reasons.
   * `/compose` and `/me/edit` hold words nobody has sent yet, so a gesture that can
   * only cost something has no business over them. `/dove` is the one that would have
   * been a bug: it fills the viewport, so its scroller is permanently at the top, and
   * every downward drag across the sill — the drag that pokes the bird — would have
   * been read as a pull and had its touches cancelled.
   */
  const client = useQueryClient();
  const refresh = useCallback(
    () => client.refetchQueries({ type: 'active' }),
    [client],
  );
  const pull = usePullToRefresh({
    enabled: !ownsHeight(pathname),
    onRefresh: refresh,
  });

  /*
   * THE ROOT HAS TWO HONEST ANSWERS NOW (docs/5 § D260901-1452).
   *
   * A resolved member still gets the feed at `/`. A stranger gets the public
   * product explanation only after the session confirms they are signed out.
   * Pending and failed reads stay in Gate: an unknown session must not flash
   * marketing to a returning member (docs/5 § D260923-2002). `?auth=signin`
   * is the one explicit handoff back to the existing gate; it is a view state,
   * not a second authentication route, and the full reload from the landing link
   * makes this plain URL read sufficient and deterministic.
   *
   * `needsOnboarding` IS NOT A STRANGER, and this is the one case the URL cannot
   * report. `viewer` is null until an account exists, and social sign-in returns
   * the browser to the bare origin — `startSocialSignIn` passes
   * `window.location.origin`, which carries no `?auth=signin`. Without this test
   * a brand-new account lands on the marketing page instead of the onboarding it
   * still owes, which `Gate` already answers.
   *
   * This return sits AFTER every shell hook so the hook order never changes when
   * `/me` resolves and swaps a returning member from the public surface to Feed.
   */
  const signingIn =
    new URLSearchParams(window.location.search).get('auth') === 'signin';
  /* A stranger the session has actually answered for: read finished, no failure,
     no account and no account owed. Anything less than all four is an unknown
     session, which `Gate` holds rather than guesses at. */
  const confirmedStranger = !loading && !error && !viewer && !needsOnboarding;
  if (pathname === '/' && confirmedStranger && !signingIn) {
    return (
      <Suspense fallback={<div className="min-h-dvh bg-background" />}>
        <LandingScreen />
      </Suspense>
    );
  }

  return (
    /* The column's own edges, on a screen wider than the column. Half strength:
       they exist to say where the phone-shaped surface ends, which does not
       require a line you look at. */
    <div className="mx-auto flex h-full max-w-xl flex-col border-border/60 sm:border-x">
      <Gate open={joining || checkingRoute}>
      {/*
        The scroll container is this element, not the window: the tab bar has
        to stay put while the feed moves, and the feed's own sticky header has
        to stick to the top of the list rather than the top of the viewport.
      */}
      {/*
        `pb-10` IS THE TAIL GAP, AND IT BELONGS HERE. Every screen used to end
        with a paragraph explaining that the app had no backend, and those
        paragraphs were incidentally providing the space at the bottom of a
        scroll. Deleting them exposed the gap as a property of the SCROLLER —
        which is this element, the one place every route is rendered into. Five
        screens each appending their own spacer had already produced four
        different tail heights, because two of those roots carry a `pb-8` of
        their own; a new screen would have got none at all and nobody would have
        noticed until it looked wrong.
      */}
      {/*
        AND IT IS NOT APPLIED TO A ROUTE THAT SIZES ITSELF TO THE VIEWPORT, which is
        the one exception the tail gap needs — `ownsHeight`, not `fullScreen`, so it
        covers the two shapes that qualify: a screen that hides the tab bar and
        anchors its own control at the bottom (`/compose`, `/join/:code`), and a
        screen that fills the height and keeps the bar (`/dove`). Neither is a scroll
        with a tail. 40px of padding under one either steals height from a
        fixed-height column or, worse, leaves a dead band beneath a call to action.
        It also made `/join` sit 40px shallower than the sign-in screen it hands over
        to, because the gate's other branches render outside this element and bypass
        the padding entirely.
      */}
      <main
        ref={pull.ref}
        /* WHAT THE ROUTER RESTORES (docs/5 § D260809-0359). Named rather than found by
           position: without the attribute the scroll cache keys the element by its
           `nth-child` path from the root, and this element's path changes the day
           anything is added above it — silently, because a selector that no longer
           matches simply restores nothing. The router names the same constant. */
        data-scroll-restoration-id={SCROLLER_ID}
        className={cn(
          'flex-1 overflow-y-auto overscroll-contain',
          !ownsHeight(pathname) && 'pb-10',
        )}
      >
        {/* First in the scroller and zero-height, so it costs no layout: see
            `PullIndicator`. */}
        <PullIndicator {...pull} />
        <Outlet />
      </main>

      {!fullScreen && (
        <nav
          aria-label="Primary"
          /*
            NO RULE ALONG THE TOP. The bar is already separated from the page by
            being a bar: it does not scroll, it is the only thing at that end of
            the screen, and the blur behind it does the work a line was doing.
            Translucent rather than solid, so content passing underneath is
            visibly passing underneath rather than being cut off at a border.
          */
          className="flex shrink-0 bg-background/85 backdrop-blur-xl"
        >
          {TABS.map((tab, i) => {
            /* A masked comments route is nested under the surface it rose from.
               Keep that surface's tab selected under the dimmer rather than
               making every destination look inactive while its place is still
               visibly underneath the sheet. */
            const active = pathname === tab.to ||
              routeIds.includes(tab.to === '/' ? FEED_MATCH_ID : tab.to);
            const Icon = tab.icon;
            /* `BADGED_TABS` names which fact each tab wears; a route absent from
               it can never badge, which is the Feed's and Dove's guarantee. */
            const badge = BADGED_TABS[tab.to as keyof typeof BADGED_TABS];
            const badged = Boolean(badge) && unread[badge.fact];
            return (
              <Fragment key={tab.to}>
                <Link
                  to={tab.to}
                  aria-current={active ? 'page' : undefined}
                  /*
                    THE NAME IS NOT DRAWN ANY MORE, SO IT IS CARRIED HERE
                    (docs/5 § D260803-1953). Four glyphs with four words under them was
                    the label doing the naming and the icon doing nothing; a
                    screen reader still needs the word, and this is where it
                    goes when the eye no longer gets it.

                    AND THE DOT IS SAID IN WORDS, because a dot is not readable
                    and `aria-label` on the link swallows anything a descendant
                    could add. Each badged tab says its OWN fact — `BADGED_TABS`
                    holds the phrase beside the flag, so the two cannot drift and
                    a tab cannot be badged without being sayable. Nothing is
                    announced as a quantity on either: they say some, never how
                    many (docs/5 § D260801-1119).
                  */
                  aria-label={
                    badged ? `${tab.label}, ${badge.said}` : tab.label
                  }
                  className={cn(
                    'group flex flex-1 items-center justify-center py-2 transition-colors',
                    active
                      ? 'text-foreground'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {/*
                    A pill behind the active icon rather than a heavier glyph
                    alone. The old cue was `fill-foreground/10` plus a thicker
                    stroke, which is a 10% wash inside a 21px outline — legible
                    if you are looking for it and invisible at a glance. A
                    filled shape behind it is the same idea at a size the eye
                    catches, and it is a shape the segmented tabs on the profile
                    now rhyme with.

                    IT GREW WITH THE LABELS' REMOVAL, from 48×28 to the create
                    control's own width. The pill was sized to sit above a word
                    and be narrower than it; alone in the row it is the only
                    thing that says which tab you are on, and a 28px-tall wash
                    under a 21px glyph in a 56px-tall bar reads as a rendering
                    artefact rather than as a state.

                    STILL FULLY ROUNDED, and now that matters: it is 56px wide,
                    exactly the create control's width, so if it took the card
                    radius too the two would be the same rectangle a shade apart
                    and the one thing in this bar that is not a tab would read as
                    the active one. A pill and an envelope cannot be confused.
                  */}
                  <span
                    className={cn(
                      'relative flex h-9 w-14 items-center justify-center rounded-full transition-colors',
                      active && 'bg-muted',
                    )}
                  >
                    <Icon
                      aria-hidden
                      className={cn(
                        'size-[1.3rem]',
                        active && 'fill-foreground/10',
                      )}
                      strokeWidth={active ? 2.2 : 1.8}
                    />
                    {badged && (
                      /*
                        THE SAME DOT THE BELL WEARS, and deliberately identical to
                        it: 8px, `primary`, punched out of the glyph behind it by a 2px
                        ring (docs/5 § D260808-2012). The two are one fact at two
                        altitudes — the tab says which tab, the bell says which
                        control — so they cannot be two marks.

                        `primary` is the same measured blue used by links and the
                        active heart, so every active social interaction has one
                        colour across the app.

                        POSITIONED AGAINST THE PILL RATHER THAN THE GLYPH, which is
                        what the `relative` above is for, and the two offsets are
                        measured: the pill is 56 × 36 and the icon 20.8, so the
                        glyph's top trailing corner sits 17.6px in and 7.6px down,
                        and 12px / 6px puts the dot just outside it — clear of the
                        stroke, 10px short of the pill's own edge. `end`, so RTL
                        mirrors it with no second rule.

                        THE RING TAKES THE COLOUR IT IS PUNCHING THROUGH. An active
                        tab is a `muted` pill, and a background-coloured halo on it
                        reads as a hole rather than as separation — the bell never
                        had to answer this, because it sits on the page.
                      */
                      <span
                        aria-hidden
                        className={cn(
                          'absolute end-3 top-1.5 size-2 rounded-full bg-primary ring-2',
                          active ? 'ring-muted' : 'ring-background',
                        )}
                      />
                    )}
                  </span>
                </Link>
                {i === CREATE_AFTER && <CreateButton />}
              </Fragment>
            );
          })}
        </nav>
      )}
      </Gate>
    </div>
  );
}

/**
 * The wordmark. The pigeon here is the default plumage rather than the signed-in
 * user's; this is the product's mark, not a person's bird.
 */
export function Wordmark() {
  return (
    <span className="flex items-center gap-2">
      <Pigeon className="w-[1.4rem]" />
      <span className="text-[17px] font-semibold tracking-tight">Dove</span>
    </span>
  );
}
